import { type RefObject, useCallback, useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { useQuery } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { listSupportedExtensions } from '@jotta/grpc-connect-openapi/photoQuery'
import { useDropZone } from '@jotta/hooks'
import { useGlobalFilePasteListener } from '@jotta/hooks/useGlobalFilePasteListener'
import { Button } from '@jotta/ui/Button'
import { Fab } from '@jotta/ui/Fab'
import SvgArrowUp from '@jotta/ui/SvgArrowUp'
import { showToast } from '@jotta/ui/ToastProvider'
import { cn } from '@jotta/utils/css'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { getFileExtension } from '@jotta/utils/files'

import { usePhotoUploadStore } from '../../store/PhotoContext'
import { PhotoUploadOverlay } from '../PhotoUploadOverlay/PhotoUploadOverlay'
import { UploadPhotoQueueStatus } from '../UploadPhotoQueueStatus/UploadPhotoQueueStatus'

type ButtonType = 'FAB' | 'TONAL' | 'GHOST'
type UploadPhotosProps = {
  btnType?: ButtonType
  onUploadsComplete?: (idsByAlbum: Record<string, string[]>) => void
}

const ButtonSwitch = ({
  btnType,
  handleClick,
}: {
  btnType: ButtonType
  handleClick: () => void
}) => {
  switch (btnType) {
    case 'FAB':
      return (
        <Fab
          onClick={handleClick}
          data-testid="PhotoFabButton"
          aria-label={t`Upload images and videos`}
        />
      )
    case 'TONAL':
    case 'GHOST':
      return (
        <Button
          onClick={handleClick}
          className={cn('btn', {
            'btn-tonal': btnType === 'TONAL',
            'btn-ghost': btnType === 'GHOST',
          })}
          icon={SvgArrowUp}
        >
          <Trans>Upload photos</Trans>
        </Button>
      )
    default:
      return exhaustiveGuard(btnType)
  }
}

export const UploadPhotosButton = ({
  btnType,
  onClick,
}: {
  btnType: ButtonType
  onClick: () => void
}) => <ButtonSwitch btnType={btnType} handleClick={onClick} />

export function UploadPhotos({
  btnType,
  onUploadsComplete,
}: UploadPhotosProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <>
      {btnType && (
        <UploadPhotosButton
          btnType={btnType}
          onClick={() => inputRef.current?.click()}
        />
      )}
      <PhotoUploader
        inputRef={inputRef}
        onUploadsComplete={onUploadsComplete}
      />
    </>
  )
}

export const PhotoUploader = observer<{
  inputRef: RefObject<HTMLInputElement | null>
  onUploadsComplete?: UploadPhotosProps['onUploadsComplete']
}>(function PhotoUploader({ inputRef, onUploadsComplete }) {
  const { data: extensions } = useQuery(
    listSupportedExtensions,
    {},
    {
      throwOnError: false,
      select: data => data.extensions.map(ext => ext.toLowerCase()),
    },
  )
  const accept =
    extensions && extensions.length > 0
      ? extensions.map(ext => `.${ext}`).join(',')
      : 'video/*,image/*'
  const acceptFilter = useCallback(
    (fileName: string) =>
      !extensions ||
      extensions.includes(getFileExtension(fileName).toLowerCase()),
    [extensions],
  )

  const uploadStore = usePhotoUploadStore()
  const isDragging = useDropZone({
    onAcceptFiles: uploadStore.upload,
    onError: uploadStore.onReject,
    filter: {
      accept: acceptFilter,
      onRejectFiles: files =>
        uploadStore.onReject(files.map(file => file.file.name)),
    },
  })

  const uploadIdsByAlbumId = uploadStore.uploadIdsByAlbumId

  useEffect(() => {
    if (onUploadsComplete && uploadIdsByAlbumId) {
      onUploadsComplete(uploadIdsByAlbumId)
    }
  }, [onUploadsComplete, uploadIdsByAlbumId])

  useGlobalFilePasteListener(
    files => {
      uploadStore.upload(files)
    },
    () => {
      showToast({
        message: t`Some files were not uploaded due to an error`,
        hasCloseButton: true,
      })
    },
  )

  return (
    <>
      <input
        onChange={uploadStore.onFileInputChange}
        data-testid="file-input"
        ref={inputRef}
        multiple
        accept={accept}
        hidden
        id="uploadInput"
        type="file"
        name="uploadInput"
        aria-label={t`Upload images and videos`}
      />
      <PhotoUploadOverlay open={isDragging} />
    </>
  )
})

export const UploadPhotosStatus = observer(function UploadPhotosStatus() {
  const uploadStore = usePhotoUploadStore()
  return (
    <UploadPhotoQueueStatus
      status={uploadStore.status}
      onCancel={uploadStore.cancel}
      onClose={uploadStore.clear}
    />
  )
})
